"use client";

import { createContext } from "react";

/**
 * Translation key using dot-notation (e.g., "menu.expand", "auth.login.button")
 */
export type TranslationKey = string;

export type ValidationResult = {
  success: boolean;
  errors?: string[];
  value: unknown;
  name?: string;
};
export type FormContextData = Record<string, unknown>;
export type FormData = Record<string, unknown>;
export type ValidationErrors = Record<string, TranslationKey[]>;

export type FormFieldSnapshot = {
  value: unknown;
  errors: TranslationKey[];
};

export type FormFieldsSnapshot = Record<string, FormFieldSnapshot>;

/**
 * Metadata about a form field, registered alongside validation.
 * Used by the AI agent to understand what each field represents.
 */
export type FieldMetadata = {
  /**
   * Human-readable label shown to the user (e.g. "Jouw bruto jaarsalaris")
   */
  label?: string;
  /**
   * Field input type
   */
  type?: "number" | "text" | "date" | "select" | "boolean" | "radio";
  /**
   * Rich input type from the rendering component (e.g. "money", "percentage").
   * Used by the AI agent to format values in summaries.
   */
  inputType?: string;
  /**
   * Available options for select/radio fields
   */
  options?: { value: string; label: string }[];
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Help text or description (e.g. "Samen kunnen jullie mogelijk meer lenen")
   */
  description?: string;
  /**
   * Business key used by update_form_fields (e.g. "income").
   * Maps to the data-field-key DOM attribute.
   */
  fieldKey?: string;
  /**
   * Wizard step key this field belongs to (auto-set via FormSectionContext)
   */
  step?: string;
  /**
   * Repeating group key this field belongs to (auto-set via FormGroupContext).
   * E.g. "mortgage-7448" for a field inside Hypotheek 1.
   */
  group?: string;
  /**
   * Human-readable group name (e.g. "Hypotheek 1")
   */
  groupName?: string;
  /**
   * Parent group key for nested repeating groups (e.g. the mortgage key
   * when this field is inside a leningdeel within a mortgage)
   */
  parentGroup?: string;
};

/**
 * Complete field schema including metadata and current value.
 * Returned by `getFieldSchema()` for AI agent awareness.
 */
export type FormFieldSchema = {
  /**
   * Field name (matches the key used in form data)
   */
  name: string;
  /**
   * Current field value
   */
  value: unknown;
  /**
   * Current validation errors
   */
  errors: TranslationKey[];
} & FieldMetadata;

/**
 * Result of validating a subset of form fields
 */
export type ValidateFieldsResult = {
  valid: boolean;
  errors: ValidationErrors;
  data: FormData;
};

export interface FormContextState {
  /**
   * Registers a validatable field.
   * @param getValue - Zero-side-effect function to read the field's current value
   */
  registerValidatable: (
    id: string,
    validate: () => ValidationResult,
    name?: string,
    getValue?: () => unknown,
    metadata?: FieldMetadata,
  ) => void;
  /**
   * Deregisters a validatable field.
   */
  deregisterValidatable: (id: string) => void;
  /**
   * Resets the validation state for a specific field.
   */
  resetValidatable: (id: string) => void;
  /**
   * Validates form fields. When ids are provided, validates only those fields.
   * When ids are omitted, validates all registered fields.
   */
  validateFields: (ids?: string[]) => ValidateFieldsResult;
  /**
   * Whether the form is disabled.
   */
  disabled: boolean;
  /**
   * Validation errors.
   */
  errors?: ValidationErrors;
  /**
   * Disables the form programmatically
   */
  disable: () => void;
  /**
   * Enables the form programmatically
   */
  enable: () => void;
  /**
   * Whether any field value differs from its initial value.
   */
  isDirty: boolean;
  /**
   * Re-snapshots current values as the new baseline and resets dirty state.
   */
  resetDirty: () => void;
  /**
   * Called by fields when their value changes so Form can re-check dirty state.
   */
  notifyValueChanged: () => void;
  /**
   * Returns a snapshot of all registered field values and their
   * validation errors, keyed by name (or uuid).
   */
  getFormData: () => FormFieldsSnapshot;
  /**
   * Returns the full field schema for all registered fields, including
   * metadata such as label, type, options, and required status.
   * Used by the AI agent for form-driven awareness.
   */
  getFieldSchema: () => FormFieldSchema[];
  /**
   * Incremented whenever the field schema changes (field registration,
   * deregistration, or value change). Consumers that need to re-read
   * the schema can use this as a reactive dependency instead of the
   * full context object.
   */
  schemaVersion: number;
}

/**
 * Context for the form component.
 */
export const FormContext = createContext<FormContextState | null>(null);
